
import { Asset, Person, Relationship, Institution, AssetCategory, Allocation, Sector, CategoryInfo } from '../types';
import { encryptionService } from './encryptionService';
import { supabase } from './authService';

const mapPersonToDb = (person: Person, nominatorId: string) => ({
  nominator_id: nominatorId,
  full_name: person.fullName,
  relationship: person.relationship,
  email: person.email,
  target_share: Number(person.targetShare) || 0,
  dob: person.dob || null,
  gender: person.gender || null,
  is_minor: !!person.isMinor,
  pan: person.pan || null,
  aadhaar: person.aadhaar || null,
  mobile: person.mobile || null,
  address: person.address || null,
  guardian_name: person.guardianName || null,
  guardian_relationship: person.guardianRelationship || null,
  guardian_contact: person.guardianContact || null,
  guardian_pan: person.guardianPan || null,
  updated_at: new Date().toISOString()
});

const mapDbToPerson = (row: any): Person => ({
  id: row.id,
  fullName: row.full_name,
  relationship: row.relationship as Relationship,
  email: row.email || '',
  targetShare: Number(row.target_share) || 0,
  dob: row.dob || '',
  gender: row.gender || '',
  isMinor: row.is_minor,
  pan: row.pan || '',
  aadhaar: row.aadhaar || '',
  mobile: row.mobile || '',
  address: row.address || '',
  guardianName: row.guardian_name || '',
  guardianRelationship: row.guardian_relationship || '',
  guardianContact: row.guardian_contact || '',
  guardianPan: row.guardian_pan || ''
});

const mapAssetToDb = (asset: Asset, userId: string) => ({
  user_id: userId,
  name: asset.name,
  category: asset.category,
  value: asset.value,
  institution: asset.institution,
  identifier_value: asset.customerId, 
  location_details: asset.branchDetails, 
  claim_path: asset.claimPath,
  contact_authority: asset.contactAuthority,
  status: asset.status,
  allocations: asset.allocations, 
  mode_of_holding: asset.modeOfOperation,
  nomination_status: asset.nominationStatus,
  last_updated_date: asset.lastUpdated,
  ifsc_code: asset.ifscCode,
  branch_name: asset.branchName,
  policy_status: asset.policyStatus,
  sum_assured: asset.sumAssuredRange,
  user_instructions: asset.userInstructions,
  account_type: asset.accountType,
  updated_at: new Date().toISOString()
});

const mapDbToAsset = (row: any): Asset => ({
  id: row.id,
  name: row.name,
  category: row.category as AssetCategory,
  value: Number(row.value),
  institution: row.institution,
  customerId: row.identifier_value,
  branchDetails: row.location_details,
  claimPath: row.claim_path,
  contactAuthority: row.contact_authority,
  status: row.status,
  allocations: (row.allocations || []) as Allocation[],
  modeOfOperation: row.mode_of_holding as any,
  nominationStatus: row.nomination_status,
  lastUpdated: row.last_updated_date,
  ifscCode: row.ifsc_code,
  branchName: row.branch_name,
  policyStatus: row.policy_status,
  sumAssuredRange: row.sum_assured,
  userInstructions: row.user_instructions,
  accountType: row.account_type,
  bankLogs: []
});

export const dbService = {
  // TAXONOMY
  async getTaxonomy(): Promise<Sector[]> {
    try {
      const [sectorsRes, categoriesRes] = await Promise.all([
        supabase.from('asset_sectors').select('*').order('display_order', { ascending: true }),
        supabase.from('asset_categories').select('*').order('display_order', { ascending: true })
      ]);

      if (sectorsRes.error) throw sectorsRes.error;
      if (categoriesRes.error) throw categoriesRes.error;

      const sectors = sectorsRes.data || [];
      const categories = categoriesRes.data || [];

      return sectors.map(s => ({
        id: s.id,
        name: s.name,
        categories: categories
          .filter(c => c.sector_id === s.id)
          .map(c => ({
            id: c.id,
            sector_id: c.sector_id,
            name: c.name,
            display_name: c.display_name
          }))
      }));
    } catch (e: any) {
      console.error("Taxonomy fetch failed:", e.message);
      return [];
    }
  },

  // BANK INSTITUTIONS
  async getInstitutions(): Promise<Institution[]> {
    try {
      const { data, error } = await supabase
        .from('bank_institutions')
        .select('*')
        .order('is_popular', { ascending: false })
        .order('name', { ascending: true });

      if (error) throw error;
      return (data || []).map(row => ({
        id: row.id,
        name: row.name,
        color: row.primary_color,
        logoSvg: row.logo_svg,
        isVerified: true
      }));
    } catch (e: any) {
      console.error("Institution fetch failed:", e.message);
      return [];
    }
  },

  // NOMINEES
  async getNominees(nominatorId: string): Promise<Person[]> {
    try {
      const { data, error } = await supabase
        .from('nominees')
        .select('*')
        .eq('nominator_id', nominatorId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return (data || []).map(mapDbToPerson);
    } catch (e: any) {
      console.error("Fetch nominees failed:", e.message);
      return [];
    }
  },

  async addNominee(nominatorId: string, person: Person): Promise<Person | null> {
    try {
      const { id, ...payload } = mapPersonToDb(person, nominatorId) as any;
      const { data, error } = await supabase
        .from('nominees')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return mapDbToPerson(data);
    } catch (e: any) {
      console.error("Add nominee failed:", e.message);
      return null;
    }
  },

  async updateNominee(nominatorId: string, person: Person): Promise<boolean> {
    try {
      const dbData = mapPersonToDb(person, nominatorId);
      const { error } = await supabase
        .from('nominees')
        .update(dbData)
        .eq('id', person.id)
        .eq('nominator_id', nominatorId);

      if (error) throw error;
      return true;
    } catch (e: any) {
      console.error("Update nominee failed:", e.message);
      return false;
    }
  },

  async deleteNominee(nominatorId: string, nomineeId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('nominees')
        .delete()
        .eq('id', nomineeId)
        .eq('nominator_id', nominatorId);

      if (error) throw error;
      return true;
    } catch (e: any) {
      console.error("Delete nominee failed:", e.message);
      return false;
    }
  },

  // ASSETS
  async getAssets(userId: string): Promise<Asset[]> {
    try {
      const { data, error } = await supabase
        .from('assets')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(mapDbToAsset);
    } catch (e: any) {
      console.error("Fetch assets failed:", e.message);
      return [];
    }
  },

  async addAsset(userId: string, asset: Asset): Promise<Asset | null> {
    try {
      const payload = mapAssetToDb(asset, userId);
      const { data, error } = await supabase
        .from('assets')
        .insert([payload])
        .select()
        .single();

      if (error) throw error;
      return mapDbToAsset(data);
    } catch (e: any) {
      console.error("Add asset failed:", e.message);
      return null;
    }
  },

  async updateAsset(userId: string, asset: Asset): Promise<boolean> {
    try {
      const payload = mapAssetToDb(asset, userId);
      const { error } = await supabase
        .from('assets')
        .update(payload)
        .eq('id', asset.id)
        .eq('user_id', userId);

      if (error) throw error;
      return true;
    } catch (e: any) {
      console.error("Update asset failed:", e.message);
      return false;
    }
  },

  async deleteAsset(userId: string, assetId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('assets')
        .delete()
        .eq('id', assetId)
        .eq('user_id', userId);

      if (error) throw error;
      return true;
    } catch (e: any) {
      console.error("Delete asset failed:", e.message);
      return false;
    }
  },

  async updateDiscoveryIndex(ownerId: string, ownerName: string, nominees: Person[], assets: Asset[]): Promise<void> {
    try {
      for (const person of nominees) {
        if (person.email) {
          const nomineeEmail = person.email.toLowerCase();
          const nomineeAssets = assets.filter(a => a.allocations.some(al => al.personId === person.id));
          const totalValue = nomineeAssets.reduce((sum, a) => {
            const al = a.allocations.find(x => x.personId === person.id);
            return sum + (a.value * (al?.percentage || 0) / 100);
          }, 0);

          const summary = {
            ownerName,
            relationship: person.relationship,
            totalValue,
            assetCount: nomineeAssets.length
          };

          await supabase
            .from('discovery_index')
            .upsert({
              nominee_email: nomineeEmail,
              owner_id: ownerId,
              owner_name: ownerName,
              encrypted_details: await encryptionService.encrypt(summary, nomineeEmail)
            });
        }
      }
    } catch (e: any) {
      console.error("Discovery index update failed:", e.message);
    }
  },

  async removeNomineeFromDiscovery(ownerId: string, nomineeEmail: string): Promise<void> {
    try {
      await supabase
        .from('discovery_index')
        .delete()
        .eq('owner_id', ownerId)
        .eq('nominee_email', nomineeEmail.toLowerCase());
    } catch (e: any) {
      console.error("Discovery cleanup failed:", e.message);
    }
  }
};
